export interface IncomeFormAgricultureKey {
    key: string;
    title: string;
    type: 'amount' | 'text';
    order: number;
    types?: string[];
    /** Split amount rows into dedicated return-view tables. */
    group?: 'balanceSheet';
}

const STANDARD_INCOME_TYPES = [
    'cultivation',
    'other_agricultural_income',
    'income_from_farming',
    'prod_of_tea_rubber',
    'prod_of_corn_sugar_beet',
    'income_from_farming_sro',
    'income_exp_reduced_by_sro',
];

export const INCOME_FORM_AGRICULTURE_KEYS: IncomeFormAgricultureKey[] = [
    { key: 'IA_AGRICULTURE_TYPE', title: 'Agriculture Type', type: 'text', order: 1 },
    { key: 'IA_PARTICULAR_OF_PRODUCES', title: 'Particular of Produces', type: 'text', order: 2 },
    { key: 'IA_FARMING_TYPE', title: 'Farming Type', type: 'text', order: 3, types: ['income_from_farming', 'income_from_farming_sro'] },
    { key: 'IA_SPECIAL_AGRI_TYPE', title: 'Income Type', type: 'text', order: 4, types: ['special_agricultural_income'] },
    { key: 'IA_TOTAL_CULTIVATION_AREA', title: 'Total Cultivation Area', type: 'text', order: 5 },
    { key: 'IA_UNIT_OF_CUL_AREA', title: 'Unit of Cultivation Area', type: 'text', order: 6 },
    { key: 'IA_NAME_OF_LEASING_PERSON', title: 'Name of the Leasing Person/Authority', type: 'text', order: 7, types: ['income_form_barga_adhi'] },
    { key: 'IA_TIN_OR_NID', title: 'TIN/NID', type: 'text', order: 8, types: ['income_form_barga_adhi'] },
    { key: 'IA_FARMER_ID', title: "Farmer's ID", type: 'text', order: 9, types: ['income_form_barga_adhi'] },
    { key: 'IA_PARTICULAR', title: 'Particular', type: 'text', order: 10, types: ['income_exp_reduced_by_sro'] },
    { key: 'IA_SRO_NO', title: 'SRO No./Reference', type: 'text', order: 11, types: ['income_exp_reduced_by_sro', 'income_from_farming_sro'] },
    { key: 'IA_YEAR', title: 'Year', type: 'text', order: 12, types: ['income_exp_reduced_by_sro', 'income_from_farming_sro'] },

    { key: 'IA_SALES_PROCEED', title: 'Sales Proceed', type: 'amount', order: 13, types: STANDARD_INCOME_TYPES },
    { key: 'IA_SALES_PROCEED', title: 'Sales Proceed/Receipt', type: 'amount', order: 13, types: ['special_agricultural_income'] },
    { key: 'IA_COST_OF_PRODUCTION_L', title: 'Cost of Production', type: 'amount', order: 14, types: STANDARD_INCOME_TYPES },
    { key: 'IA_OTHER_ALWBLE_DEDUCTION_L', title: 'Other Allowable Deduction', type: 'amount', order: 15, types: STANDARD_INCOME_TYPES.concat(['income_form_barga_adhi']) },
    { key: 'IA_NET_INCOME_L', title: 'Net Income', type: 'amount', order: 16, types: STANDARD_INCOME_TYPES.concat(['income_form_barga_adhi']) },
    { key: 'IA_NET_INCOME_L', title: 'Agricultural Income', type: 'amount', order: 16, types: ['special_agricultural_income'] },

    { key: 'IA_TOTAL_RECEIPT', title: 'Total Receipt to the Taxpayer', type: 'amount', order: 17, types: ['income_form_barga_adhi'] },
    { key: 'IA_TAX_EXEMPTED_INCOME', title: 'Tax Exempted Income', type: 'amount', order: 18, types: ['prod_of_corn_sugar_beet'] },
    { key: 'IA_TAX_APPLICABLE', title: 'Tax Applicable', type: 'amount', order: 19, types: ['income_exp_reduced_by_sro', 'income_from_farming_sro'] },
    { key: 'IA_TAXABLE_INCOME', title: 'Taxable Income', type: 'amount', order: 20, types: ['income_exp_reduced_by_sro', 'income_from_farming_sro'] },

    { key: 'IA_ORIGINAL_COST', title: 'Cost of Acquisition', type: 'amount', order: 21, types: ['special_agricultural_income'] },
    { key: 'IA_WRITTEN_DOWN_VALUE', title: 'Written Down Value', type: 'amount', order: 22, types: ['special_agricultural_income'] },
    { key: 'IA_CAPITAL_GAIN', title: 'Capital Gain', type: 'amount', order: 23, types: ['special_agricultural_income'] },

    { key: 'IA_AGRICULTURAL_INCOME', title: 'Agricultural Income', type: 'amount', order: 24, types: ['prod_of_tea_rubber'] },
    { key: 'IA_INCOME_FRM_BUSINESS_PROFESSION', title: 'Business Income', type: 'amount', order: 25, types: ['prod_of_tea_rubber'] },

    { key: 'IA_MAINTAIN_BOOKS_OF_ACT', title: 'Maintain Books of Accounts', type: 'text', order: 25.5, types: ['prod_of_tea_rubber'] },

    { key: 'IA_CASH_IN_HAND_AND_BANK', title: 'Cash in Hand & at Bank', type: 'amount', order: 26, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_INVENTORIES', title: 'Inventories', type: 'amount', order: 27, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_FIXED_ASSETS', title: 'Fixed Assets', type: 'amount', order: 28, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_OTHER_ASSETS', title: 'Other Assets', type: 'amount', order: 29, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_TOTAL_ASSETS', title: 'Total Assets', type: 'amount', order: 30, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_OPENING_CAPITAL', title: 'Opening Capital', type: 'amount', order: 31, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_NET_PROFIT', title: 'Net Profit', type: 'amount', order: 32, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_WITHDRAWAL_IN_INCOME_YR', title: 'Withdrawals in the Income Year', type: 'amount', order: 33, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_CLOSING_CAPITAL', title: 'Closing Capital', type: 'amount', order: 34, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_LIABILITIES', title: 'Liabilities', type: 'amount', order: 35, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
    { key: 'IA_TOTAL_CAPITAL_AND_LIABILITIES', title: 'Total Capital and Liabilities', type: 'amount', order: 36, types: ['prod_of_tea_rubber'], group: 'balanceSheet' },
];

export const AGRICULTURE_TYPE_LABELS: Record<string, string> = {
    cultivation: 'Cultivation',
    prod_of_tea_rubber: 'Production of Tea or Rubber',
    prod_of_corn_sugar_beet: 'Production of Corn or Sugar-beet',
    other_agricultural_income: 'Other Agricultural Income',
    income_form_barga_adhi: 'Income from Barga/Adhi/Bhag',
    income_exp_reduced_by_sro: 'Income Subject to Reduced Tax Rate',
    special_agricultural_income: 'Special Agricultural Income',
    income_from_farming: 'Income from Farming',
    income_from_farming_sro: 'Income from Farming (with Exemption)',
};

export const FARMING_TYPE_LABELS: Record<string, string> = {
    aquaculture: 'Aquaculture & Hydroponics',
    aviculture: 'Aviculture',
    dairy_hatchery: 'Dairy Farming',
    fish_farming: 'Fish Farming',
    floriculture_apiculture: 'Floriculture & Apiculture',
    fruit_cultivation: 'Fruit Production',
    horticulture: 'Horticulture',
    livestock: 'Livestock Farming',
    land_usage: 'Natural Uses of Land',
    nursery: 'Nursery',
    poultry_farming: 'Poultry Farming',
    reptile_farming: 'Reptile Farming',
    seed_production: 'Seed Production',
    wood_grass_shrub_prod: 'Wood, Grass & Shrub Production',
};

export const SPECIAL_AGRI_INCOME_TYPE_LABELS: Record<string, string> = {
    special_agri_sale_capital_asset: 'Sale of Capital Asset',
    special_agri_insurance_salvage_comp: 'Insurance, Salvage or Compensation Receipt',
    failure_of_deduction_56: 'Failure of deduction, collection or Payment of Source Tax [U/S 41(6)]',
    special_agri_any_adjustment: 'Any Adjustment U/S 41(6)',
};

export const FAILURE_OF_DEDUCTION_LIST_KEY = 'failureOfDeductionList';

export const FAILURE_OF_DEDUCTION_COLUMNS = [
    { key: 'headOfExpenses', title: 'Head Of Expenses', isAmount: false },
    { key: 'amount', title: 'Amount', isAmount: true },
    { key: 'tdsAmount', title: 'TDS Applicable Amount', isAmount: true },
    { key: 'tds50Parent', title: '50% of TDS', isAmount: true },
    { key: 'totalPayable', title: 'Total Payable', isAmount: true },
];
